// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import React, {memo, ReactNode, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Form, Popup} from 'semantic-ui-react';

import * as S from './ProjectPicker.styles';
import {useProjectOptions} from './useProjectOptions';

interface ProjectPickerProps {
  entityName: string;
  projectName: string;
  triggerOverride?: ReactNode;
  requireSameEntity?: boolean;
  requireSameEntityReason?: string;
  onOpen?: () => void;
  onClose?: () => void;
  projectId: string;
  setProject(entityName: string, projectName: string, id: string): void;
}

export const ProjectPickerComp: React.FC<ProjectPickerProps> = ({
  entityName,
  onClose,
  onOpen,
  projectName,
  requireSameEntity,
  requireSameEntityReason,
  setProject,
  triggerOverride,
  projectId,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const {projects, selectedProjectIdx} = useProjectOptions({
    selectedProject: {
      entityName,
      name: projectName,
      internalId: projectId,
    },
    queryString: searchQuery,
  });

  const options = projects.map((p, value) => {
    const disabled = requireSameEntity && p.entityName !== entityName;
    const showPopup = disabled && requireSameEntityReason;
    const text = `${p.entityName}/${p.name}`;
    return {
      text,
      value,
      disabled,
      content: showPopup ? (
        <Popup
          basic
          on="hover"
          size="mini"
          className="wb-table-action-popup"
          position="bottom right"
          content={
            <span className="hint-text small">{requireSameEntityReason}</span>
          }
          trigger={
            <S.DisabledDropdownOption>
              <span>{text}</span>
              <Icon name="lock-closed" />
            </S.DisabledDropdownOption>
          }
        />
      ) : (
        <span>{text}</span>
      ),
    };
  });
  // sort disabled options to the bottom of the list
  options.sort(a => (a.disabled ? 1 : -1));

  return (
    <Popup
      basic
      open={open}
      onOpen={() => {
        setOpen(true);
        onOpen?.();
      }}
      onClose={() => {
        setOpen(false);
        onClose?.();
      }}
      className="wb-table-action-popup"
      on="click"
      position="bottom left"
      popperModifiers={{
        preventOverflow: {
          // prevent popper from erroneously constraining the popup to the
          // table header
          boundariesElement: 'viewport',
        },
      }}
      trigger={
        triggerOverride ? (
          triggerOverride
        ) : (
          <Button
            variant="ghost"
            icon="folder-project"
            aria-label="Open project picker"
            twWrapperStyles={{display: 'inline'}}
            className="mr-12">
            <div className="flex">
              <div className="max-w-[5rem] truncate">{entityName}</div>/
              <div className="max-w-[8rem] truncate">{projectName}</div>
            </div>
          </Button>
        )
      }
      content={
        <Form>
          <Form.Dropdown
            aria-label="Select project for run set"
            selection
            search
            searchQuery={searchQuery}
            onSearchChange={(e, {searchQuery: newSearchQuery}) =>
              setSearchQuery(newSearchQuery)
            }
            value={selectedProjectIdx}
            options={options}
            onChange={(e, {value}) => {
              if (typeof value === 'number') {
                const proj = projects[value];
                setProject(proj.entityName, proj.name, proj.internalId);
                setOpen(false);
              }
            }}
          />
        </Form>
      }
    />
  );
};

export const ProjectPicker = memo(ProjectPickerComp);
ProjectPicker.displayName = 'ProjectPicker';
